/**
 * Pure helpers for the Recurring Builder.
 *
 * All functions here are pure: take a draft (or template), return a
 * value. No side effects, no React, no hooks. Keeps the builder
 * testable and the UI components thin.
 */

import type {
  RecurringTemplate,
  RecurringSchedule,
  RecurringParty,
  RecurringTransactionSpec,
  RecurringFrequency,
} from "../recurringTypes";
import { TXN_TYPE_LABELS } from "../recurringMockData";
import {
  calculateNextOccurrences,
  formatDateLong,
  formatAmount as formatAmountDisplay,
} from "../recurringUtils";
import type {
  BuilderDraft,
  BuilderValidationErrors,
} from "./builderTypes";
import { WEEKDAY_SHORT } from "./builderTypes";

/** YYYY-MM-DD for a Date in local time. */
function toISODate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Parse YYYY-MM-DD to a Date at local midnight. Null-safe. */
function parseISODate(s: string | null): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** 1 -> "1st", 2 -> "2nd", 21 -> "21st". */
function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/** Strip commas and whitespace from an amount string; NaN if blank or invalid. */
function parseAmount(raw: string): number {
  const cleaned = raw.replace(/,/g, "").trim();
  if (!cleaned) return NaN;
  return Number(cleaned);
}

// ---------- Frequency category helpers ----------

/** True when the frequency uses a day-of-month selector. */
export function usesMonthDay(frequency: RecurringFrequency): boolean {
  return (
    frequency === "monthly" ||
    frequency === "quarterly" ||
    frequency === "semi_annually" ||
    frequency === "annually"
  );
}

/** True when the frequency uses a day-of-week selector. */
export function usesWeekDay(frequency: RecurringFrequency): boolean {
  return frequency === "weekly" || frequency === "biweekly";
}

// ---------- Draft lifecycle ----------

/** Build an empty draft seeded with reasonable New-mode defaults. */
export function createEmptyDraft(): BuilderDraft {
  const today = new Date();
  return {
    mode: "create",
    editingTemplateId: null,
    name: "",
    recurringMode: "scheduled",
    status: "active",
    txnType: "expense",
    partyId: null,
    partyName: "",
    accountId: null,
    accountName: "",
    amount: "",
    currency: "CAD",
    memo: "",
    frequency: "monthly",
    interval: 1,
    dayOfWeek: today.getDay(),
    dayOfMonth: today.getDate(),
    startDate: toISODate(today),
    endType: "never",
    maxOccurrences: 12,
    endDate: null,
  };
}

/** Map an existing template into a draft for Edit mode. */
export function fromTemplate(template: RecurringTemplate): BuilderDraft {
  const party = template.party;
  let partyId: string | null = null;
  let partyName = "";
  if (party.type === "supplier") {
    partyId = party.supplierId ?? null;
    partyName = party.supplierName ?? "";
  } else if (party.type === "customer") {
    partyId = party.customerId ?? null;
    partyName = party.customerName ?? "";
  }

  const schedule = template.schedule;
  const endType: BuilderDraft["endType"] =
    schedule.maxOccurrences != null
      ? "after"
      : schedule.endDate != null
        ? "on"
        : "never";

  return {
    mode: "edit",
    editingTemplateId: template.id,
    name: template.name,
    recurringMode: template.mode,
    status: template.status,
    txnType: template.txnType,
    partyId,
    partyName,
    accountId: template.transaction.accountId,
    accountName: template.transaction.accountName,
    amount: String(template.transaction.amount),
    currency: template.transaction.currency,
    memo: template.transaction.memo ?? "",
    frequency: schedule.frequency,
    interval: schedule.interval ?? 1,
    dayOfWeek: schedule.dayOfWeek ?? 1,
    dayOfMonth: schedule.dayOfMonth ?? 1,
    startDate: schedule.startDate,
    endType,
    maxOccurrences: schedule.maxOccurrences ?? 12,
    endDate: schedule.endDate ?? null,
  };
}

/**
 * Convert a validated draft into a saveable RecurringTemplate.
 * Pass the existing template for edit mode so id, runCount, createdAt
 * and optional spec fields (category, gstTreatment, etc.) are preserved.
 */
export function toTemplate(
  draft: BuilderDraft,
  existing: RecurringTemplate | null,
): RecurringTemplate {
  const amount = parseAmount(draft.amount);

  const schedule: RecurringSchedule = {
    frequency: draft.frequency,
    startDate: draft.startDate,
    interval: draft.interval > 1 ? draft.interval : undefined,
    dayOfWeek: usesWeekDay(draft.frequency) ? draft.dayOfWeek : undefined,
    dayOfMonth: usesMonthDay(draft.frequency) ? draft.dayOfMonth : undefined,
    maxOccurrences:
      draft.endType === "after" ? draft.maxOccurrences : undefined,
    endDate: draft.endType === "on" ? draft.endDate ?? undefined : undefined,
  };

  const party = buildParty(draft);

  const transaction: RecurringTransactionSpec = {
    accountId: draft.accountId ?? "",
    accountName: draft.accountName,
    amount,
    currency: draft.currency || "CAD",
    memo: draft.memo.trim() || undefined,
    category: existing?.transaction.category,
    gstTreatment: existing?.transaction.gstTreatment,
    class: existing?.transaction.class,
    location: existing?.transaction.location,
    reimbursable: existing?.transaction.reimbursable,
    billable: existing?.transaction.billable,
  };

  const now = new Date().toISOString();
  const nextDates = calculateNextOccurrences(schedule, draft.startDate, 1);

  return {
    id: existing?.id ?? `rec-${Date.now()}`,
    name: draft.name.trim(),
    mode: draft.recurringMode,
    status: draft.status,
    txnType: draft.txnType,
    party,
    transaction,
    schedule,
    previousDate: existing?.previousDate ?? null,
    nextDate: nextDates[0] ?? null,
    runCount: existing?.runCount ?? 0,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    createdBy: existing?.createdBy ?? "me",
  };
}

/** Build the discriminated RecurringParty from the draft. */
function buildParty(draft: BuilderDraft): RecurringParty {
  switch (draft.txnType) {
    case "journal":
      return { type: "internal" };
    case "transfer":
      return { type: "transfer" };
    case "invoice":
      return {
        type: "customer",
        customerId: draft.partyId ?? "",
        customerName: draft.partyName,
      };
    case "expense":
    case "bill":
      return {
        type: "supplier",
        supplierId: draft.partyId ?? "",
        supplierName: draft.partyName,
      };
  }
}

// ---------- Validation ----------

/** Validate a draft; empty object means ready to save. */
export function validateDraft(draft: BuilderDraft): BuilderValidationErrors {
  const errors: BuilderValidationErrors = {};

  if (!draft.name.trim()) {
    errors.name = "Template name is required.";
  }

  const needsParty =
    draft.txnType !== "journal" && draft.txnType !== "transfer";
  if (needsParty && !draft.partyId) {
    errors.partyId =
      draft.txnType === "invoice" ? "Choose a customer." : "Choose a supplier.";
  }

  if (!draft.accountId) {
    errors.accountId = "Choose an account.";
  }

  const amount = parseAmount(draft.amount);
  if (isNaN(amount) || amount <= 0) {
    errors.amount = "Enter an amount greater than zero.";
  }

  if (!draft.startDate) {
    errors.startDate = "Pick a start date.";
  }

  if (usesMonthDay(draft.frequency)) {
    const dom = draft.dayOfMonth;
    if (dom !== "last" && (dom < 1 || dom > 31)) {
      errors.dayOfMonth = "Day of month must be 1 to 31, or last.";
    }
  }

  if (usesWeekDay(draft.frequency)) {
    if (draft.dayOfWeek < 0 || draft.dayOfWeek > 6) {
      errors.dayOfWeek = "Pick a day of the week.";
    }
  }

  if (draft.endType === "after") {
    if (
      !Number.isFinite(draft.maxOccurrences) ||
      draft.maxOccurrences < 1 ||
      draft.maxOccurrences > 999
    ) {
      errors.maxOccurrences = "Enter 1 to 999 occurrences.";
    }
  }

  if (draft.endType === "on") {
    if (!draft.endDate) {
      errors.endDate = "Pick an end date.";
    } else {
      const end = parseISODate(draft.endDate);
      const start = parseISODate(draft.startDate);
      if (end && start && end < start) {
        errors.endDate = "End date must be on or after the start date.";
      }
    }
  }

  return errors;
}

/** True when the draft passes validation. */
export function isValidDraft(draft: BuilderDraft): boolean {
  return Object.keys(validateDraft(draft)).length === 0;
}

// ---------- Preview helpers ----------

/** One-line human description of the schedule. */
export function formatScheduleLine(draft: BuilderDraft): string {
  switch (draft.frequency) {
    case "daily":
      return draft.interval > 1 ? `Every ${draft.interval} days` : "Every day";
    case "weekly":
      return `Weekly on ${WEEKDAY_SHORT[draft.dayOfWeek]}`;
    case "biweekly":
      return `Every other ${WEEKDAY_SHORT[draft.dayOfWeek]}`;
    case "monthly":
      return draft.dayOfMonth === "last"
        ? "Monthly on the last day"
        : `Monthly on the ${ordinal(draft.dayOfMonth)}`;
    case "quarterly":
      return draft.dayOfMonth === "last"
        ? "Quarterly, end of quarter"
        : `Quarterly, ${ordinal(draft.dayOfMonth as number)} of quarter`;
    case "semi_annually":
      return "Twice yearly";
    case "annually":
      return "Yearly, same day as start date";
    case "custom":
      return "Custom schedule";
  }
}

/** End-clause fragment, e.g., "no end date" or "ending after 12 occurrences". */
export function formatEndClause(draft: BuilderDraft): string {
  switch (draft.endType) {
    case "never":
      return "no end date";
    case "after":
      return `ending after ${draft.maxOccurrences} occurrence${draft.maxOccurrences === 1 ? "" : "s"}`;
    case "on":
      return draft.endDate
        ? `ending on ${formatDateLong(draft.endDate)}`
        : "no end date set";
  }
}

/** The full prose summary shown in Section 4 (Preview). */
export function formatPreviewSummary(draft: BuilderDraft): string {
  const schedule = formatScheduleLine(draft);
  const start = draft.startDate
    ? formatDateLong(draft.startDate)
    : "a start date you pick";
  const end = formatEndClause(draft);
  const amount = draft.amount
    ? formatAmountDisplay(parseAmount(draft.amount) || 0)
    : "0.00";
  const txnName = TXN_TYPE_LABELS[draft.txnType];
  const partyClause =
    draft.txnType === "journal" || draft.txnType === "transfer"
      ? ""
      : ` to ${draft.partyName || "the party"}`;
  const accountName = draft.accountName || "an account";

  return `${schedule}, starting ${start}, ${end}. Posts a ${txnName}${partyClause} for $${amount} against ${accountName}.`;
}

/** Compute the next N occurrence dates from the draft. Returns ISO date strings. */
export function computeNextOccurrences(
  draft: BuilderDraft,
  count = 3,
): string[] {
  const schedule: RecurringSchedule = {
    frequency: draft.frequency,
    startDate: draft.startDate,
    interval: draft.interval > 1 ? draft.interval : undefined,
    dayOfWeek: usesWeekDay(draft.frequency) ? draft.dayOfWeek : undefined,
    dayOfMonth: usesMonthDay(draft.frequency) ? draft.dayOfMonth : undefined,
    maxOccurrences:
      draft.endType === "after" ? draft.maxOccurrences : undefined,
    endDate: draft.endType === "on" ? draft.endDate ?? undefined : undefined,
  };
  return calculateNextOccurrences(schedule, draft.startDate, count);
}

// ---------- Dirty-state helper ----------

/** True when the draft differs from its initial state (confirm-before-close). */
export function isDirty(draft: BuilderDraft, initial: BuilderDraft): boolean {
  return JSON.stringify(draft) !== JSON.stringify(initial);
}